/**
 * @fileoverview Mock backend for User Management.
 * @description Registers GET /users (the user directory, which invitations add to), GET
 * /users/summary, GET /users/filter-options, GET /users/me/team and its summary (the whole mock
 * directory as the team, with a made-up but stable bank count per member and no targets), GET and
 * PATCH /users/{userId} (with its ETag), GET /users/{userId}/edit-options, GET /users/configuration
 * (the lifecycle reasons) and POST /users/{userId}/deactivate and /reactivate on this feature's
 * mock adapter, answering in the backend's envelopes and with its error codes. Each directory entry
 * carries the user's state and the actions the caller may take on them. Lives with the
 * user-invitation mocks because they share the directory. The actions and the commands are
 * narrowed by the signed-in caller's authority as the backend narrows them (never their own row,
 * only users below them unless they are the Super Admin, with the privilege each needs; see
 * user-management-mock-store.ts), while the lists still show the whole directory. A no-op when
 * VITE_ENABLE_MOCK_API is false.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 * @author Anshul Rokde
 */

import { fail, ok, page, queryParams } from './mock-response.utils'
import { userInvitationsMockAdapter } from './user-invitations-mock-adapter'
import {
  applyDeactivation,
  applyReactivation,
  applyUpdate,
  etagOf,
  findManagedUser,
  listManagedUsers,
  managerEditOptions,
  managerFilterOptions,
  roleEditOptions,
  roleFilterOptions,
  stateFilterOptions,
  summarize,
  toDirectoryEntry,
  toUserDetail,
  userManagementConfiguration,
} from './user-management-mock-store'

import type { MockDirectoryUser } from './user-invitations-mock-store'
import type {
  MockDeactivateUserRequest,
  MockReactivateUserRequest,
  MockUpdateUserRequest,
} from './user-management-mock-store'
import type { AxiosRequestConfig } from 'axios'

const USER_PATH = /^\/users\/(?!summary$|filter-options$|configuration$|eligible-)[^/]+$/
const EDIT_OPTIONS_PATH = /^\/users\/[^/]+\/edit-options$/
const DEACTIVATE_PATH = /^\/users\/[^/]+\/deactivate$/
const REACTIVATE_PATH = /^\/users\/[^/]+\/reactivate$/

/**
 * @description Pulls the user id out of a /users/{userId}[/...] URL.
 * @param {AxiosRequestConfig} config - The request config.
 * @returns {string} The user id.
 */
function userIdFrom(config: AxiosRequestConfig): string {
  return decodeURIComponent(config.url?.split('/')[2] ?? '')
}

/**
 * @description Whether a directory user matches GET /users' filters.
 * @param {MockDirectoryUser} user - The user.
 * @param {Record<string, string | undefined>} params - The query params.
 * @returns {boolean} True when every given filter matches.
 */
function matchesFilters(
  user: MockDirectoryUser,
  params: Record<string, string | undefined>,
): boolean {
  const needle = params.search?.trim().toLowerCase() ?? ''
  const haystack = [user.displayName, user.middleName, user.email, user.employeeCode]
    .join(' ')
    .toLowerCase()
  return (
    (!needle || haystack.includes(needle)) &&
    (!params.status || user.status === params.status) &&
    (!params.directoryStatus || user.directoryStatus === params.directoryStatus) &&
    (!params.roleId || user.roles[0]?.roleId === params.roleId) &&
    (!params.reportingManagerUserId ||
      user.reportingManager?.userId === params.reportingManagerUserId) &&
    // ALL_INDIA is the members with no state.
    (!params.stateId ||
      (params.stateId === 'ALL_INDIA'
        ? user.state === null
        : user.state?.stateId === params.stateId))
  )
}

/**
 * @description Sorts the directory like GET /users: display name, employee code or joining date,
 * a leading "-" descending, with no value sorting last either way.
 * @param {MockDirectoryUser[]} users - The users.
 * @param {string | undefined} sort - The sort param.
 * @returns {MockDirectoryUser[]} The sorted users.
 */
function sortUsers(users: MockDirectoryUser[], sort = 'displayName'): MockDirectoryUser[] {
  const direction = sort.startsWith('-') ? -1 : 1
  const field = sort.replace(/^-/, '') as 'displayName' | 'employeeCode' | 'dateOfJoining'
  return [...users].sort((a, b) => {
    const left = a[field]
    const right = b[field]
    if (left === right) return 0
    if (left === null) return 1
    if (right === null) return -1
    return direction * left.localeCompare(right)
  })
}

/**
 * @description Reads a request's If-Match header.
 * @param {AxiosRequestConfig} config - The request config.
 * @returns {string} The header, or '' when absent.
 */
function ifMatchFrom(config: AxiosRequestConfig): string {
  const header = (config.headers as Record<string, unknown> | undefined)?.['If-Match']
  return typeof header === 'string' ? header : ''
}

/**
 * @description A made-up but stable number of banks (0-8) for a mock team member.
 * @param {string} userId - The member.
 * @returns {number} Their assigned banks.
 */
function mockAssignedBanks(userId: string): number {
  return [...userId].reduce((sum, char) => sum + char.charCodeAt(0), 0) % 9
}

if (userInvitationsMockAdapter) {
  const adapter = userInvitationsMockAdapter

  adapter.onGet('/users/me/team').reply((config) => {
    const params = queryParams(config)
    const users = listManagedUsers().filter((user) => matchesFilters(user, params))
    const members = sortUsers(users, params.sort).map((user) => ({
      ...user,
      isSelf: false,
      workload: {
        currentAssignedTenants: mockAssignedBanks(user.userId),
        incompleteTargets: 0,
        overdueTargets: 0,
      },
    }))
    return page(config, members)
  })

  adapter.onGet('/users/me/team/summary').reply(() => {
    const { salesHeads, salesManagers, salesExecutives, evaluatedAt } = summarize()
    return [
      200,
      ok({
        teamSize: salesHeads + salesManagers + salesExecutives,
        platformAdministrators: 0,
        salesHeads,
        salesManagers,
        salesExecutives,
        assignedTenants: listManagedUsers().reduce(
          (sum, user) => sum + mockAssignedBanks(user.userId),
          0,
        ),
        overdueTargets: 0,
        evaluatedAt,
        workloadEvaluatedAt: evaluatedAt,
      }),
    ]
  })

  adapter.onGet('/users').reply((config) => {
    const params = queryParams(config)
    const users = listManagedUsers().filter((user) => matchesFilters(user, params))
    return page(config, sortUsers(users, params.sort).map(toDirectoryEntry))
  })

  adapter.onGet('/users/summary').reply(() => [200, ok(summarize())])

  adapter.onGet('/users/filter-options').reply((config) => {
    const { kind, context } = queryParams(config)
    const options: object[] =
      kind === 'ROLE'
        ? roleFilterOptions()
        : kind === 'STATE'
          ? stateFilterOptions()
          : managerFilterOptions()
    return page(config, options, (items) => ({ context: context ?? 'DIRECTORY', kind, items }))
  })

  adapter.onGet('/users/configuration').reply(() => [200, ok(userManagementConfiguration())])

  adapter.onGet(EDIT_OPTIONS_PATH).reply((config) => {
    const user = findManagedUser(userIdFrom(config))
    if (!user) {
      return fail(404, '3001', 'User not found')
    }
    if (user.status !== 'ACTIVE') {
      return fail(409, '3023', 'User update is not permitted in the current state')
    }
    const { kind, primaryRoleId } = queryParams(config)
    const options: object[] =
      kind === 'ROLE' ? roleEditOptions() : managerEditOptions(user.userId, primaryRoleId ?? '')
    return page(config, options, (items) => ({
      kind,
      items,
      fixedDepartment: {
        departmentId: 'mock-department-sales',
        departmentCode: 'SALES',
        name: 'Sales',
      },
    }))
  })

  adapter.onGet(USER_PATH).reply((config) => {
    const user = findManagedUser(userIdFrom(config))
    return user
      ? [200, ok(toUserDetail(user)), { etag: etagOf(user.userId) }]
      : fail(404, '3001', 'User not found')
  })

  adapter.onPatch(USER_PATH).reply((config) => {
    const userId = userIdFrom(config)
    const user = findManagedUser(userId)
    if (!user) {
      return fail(404, '3001', 'User not found')
    }

    const request = JSON.parse(config.data as string) as MockUpdateUserRequest
    const refusal = applyUpdate(user, request, ifMatchFrom(config))
    if (refusal) {
      return fail(...refusal)
    }

    const updated = findManagedUser(userId) ?? user
    return [200, ok(toUserDetail(updated)), { etag: etagOf(userId) }]
  })

  adapter.onPost(DEACTIVATE_PATH).reply((config) => {
    const userId = userIdFrom(config)
    const user = findManagedUser(userId)
    if (!user) {
      return fail(404, '3001', 'User not found')
    }

    const request = JSON.parse(config.data as string) as MockDeactivateUserRequest
    const refusal = applyDeactivation(user, request, ifMatchFrom(config))
    if (refusal) {
      return fail(...refusal)
    }

    const deactivated = findManagedUser(userId) ?? user
    return [200, ok(toUserDetail(deactivated)), { etag: etagOf(userId) }]
  })

  adapter.onPost(REACTIVATE_PATH).reply((config) => {
    const userId = userIdFrom(config)
    const user = findManagedUser(userId)
    if (!user) {
      return fail(404, '3001', 'User not found')
    }

    const request = JSON.parse(config.data as string) as MockReactivateUserRequest
    const outcome = applyReactivation(user, request, ifMatchFrom(config))
    if (Array.isArray(outcome)) {
      return fail(...outcome)
    }

    const reactivated = findManagedUser(userId) ?? user
    return [
      200,
      ok({ ...toUserDetail(reactivated), reactivationMode: outcome, deliveryStatus: 'QUEUED' }),
      { etag: etagOf(userId) },
    ]
  })
}
